import { z } from 'zod';
import { CONTENT_PRIORITIES, CONTENT_SEASONS, HASHTAG_PATTERN, MULTILINGUAL_LANGUAGE } from './content.js';
import { CAMPAIGN_ROLES } from '../campaigns.js';
import { CONTENT_VARIANT_FORMATS } from './formats.js';
import { VARIANT_MEDIA_FITS } from './media-fit.js';
/** Rounding slack for crops computed in the browser (`0.1 + 0.2`). */
const EPSILON = 1e-9;
const unit = z.number().min(0).max(1);
/**
 * Crop of one variant media item, relative to the original (spec 40 §4.5):
 * `x + width <= 1`, `y + height <= 1`, width and height above 0.
 */
export const variantCropSchema = z
    .object({ x: unit, y: unit, width: unit.gt(0), height: unit.gt(0) })
    .strict()
    .refine((crop) => crop.x + crop.width <= 1 + EPSILON && crop.y + crop.height <= 1 + EPSILON, { message: 'invalid_crop' });
const uuid = z.string().uuid();
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const snapshotItemSchema = z
    .object({
    title: z.string(),
    description: z.string().nullable(),
    property_id: uuid,
    pillar_id: uuid.nullable(),
    campaign_id: uuid.nullable(),
    /** Absent in versions from before E300 S300-2 (read as `null`). */
    campaign_role: z.enum(CAMPAIGN_ROLES).nullable().optional(),
    evergreen: z.boolean(),
    season: z.enum(CONTENT_SEASONS).nullable(),
    priority: z.enum(CONTENT_PRIORITIES),
    desired_date: day.nullable(),
    /** Wall time `HH:mm` next to `desired_date` (E510); absent in versions from before it (read as `null`). */
    desired_time: z.string().regex(/^\d{2}:\d{2}$/).nullable().optional(),
})
    .strict();
const snapshotLocalizationSchema = z
    .object({
    id: uuid,
    language: z.string().min(1),
    languages: z.array(z.string().min(1)).min(1),
    headline: z.string(),
    caption: z.string(),
    cta: z.string(),
    hashtags: z.array(z.string().regex(HASHTAG_PATTERN)),
})
    .strict()
    .refine((loc) => (loc.language === MULTILINGUAL_LANGUAGE ? loc.languages.length >= 2 : loc.languages.length === 1 && loc.languages[0] === loc.language), {
    message: 'languages_mismatch',
});
/** `fit` is absent in snapshots from before E580 (ADR-042). */
const snapshotMediaSchema = z
    .object({ asset_id: uuid, position: z.number().int().min(0), crop: variantCropSchema.nullable(), fit: z.enum(VARIANT_MEDIA_FITS).nullable().optional() })
    .strict();
/** `linked_to_variant_id` is `null` in snapshots from before E260 (spec 40 §4.4d, ADR-029). */
const snapshotVariantSchema = z
    .object({
    id: uuid,
    format: z.enum(CONTENT_VARIANT_FORMATS),
    linked_to_variant_id: uuid.nullable().optional(),
    media: z.array(snapshotMediaSchema),
})
    .strict();
/**
 * The full content of one version (spec 40 §4.6, `schema_version: 1`). Status,
 * `approved_version_id` and `origin` are workflow state, not content, and are
 * not part of it; soft-deleted localizations and variants are omitted.
 * `contributions` holds the parts of registered contributors (module 90
 * `designs`, module 100 `storyboards`), keyed by contributor.
 */
export const contentSnapshotSchema = z
    .object({
    schema_version: z.literal(1),
    item: snapshotItemSchema,
    localizations: z.array(snapshotLocalizationSchema),
    variants: z.array(snapshotVariantSchema),
    contributions: z.record(z.string().regex(/^[a-z][a-z_]{0,39}$/), z.unknown()),
})
    .strict();
//# sourceMappingURL=snapshot.js.map